import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useProgressData } from './useProgressData';

export const RegionsChart = memo(function RegionsChart() {
  const { regionResultCounts, regionResultCountsByFunding, filteredResults } = useProgressData();
  const yAxisWidth = useResponsiveYAxisWidth(150);

  if (!regionResultCounts || Object.keys(regionResultCounts).length === 0) return (
    <ChartWrapper title="Results by Region">
      <NoChartData message="No region data on results" detail="This chart shows results by CGIAR region. None of the results in the current selection have region data assigned." />
    </ChartWrapper>
  );

  const chartData = Object.entries(regionResultCounts)
    .map(([name, count]) => {
      const funding = regionResultCountsByFunding[name] || { W1W2: 0, W3: 0 };
      return {
        name: name.length > 25 ? name.substring(0, 23) + '...' : name,
        fullName: name,
        w1w2: funding.W1W2,
        w3: funding.W3,
        total: count,
      };
    })
    .sort((a, b) => b.total - a.total);

  const renderStackTotal = (props) => {
    const { x, y, width, height, index } = props;
    const entry = chartData[index];
    if (!entry) return null;
    const total = entry.w1w2 + entry.w3;
    if (total === 0) return null;
    return (
      <text x={x + width + 4} y={y + height / 2} fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} dominantBaseline="middle">
        {total}
      </text>
    );
  };

  return (
    <ChartWrapper title="Results by Region" info="Results distributed across world regions, split by funding type. A single result may be tagged to multiple regions." chartData={chartData} exportFilename="results-by-region" sourceResults={filteredResults}>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 150 ? 9 : 11 }} width={yAxisWidth} />
          <Tooltip
            formatter={(value, name) => [value, name === 'w1w2' ? 'W1/W2 Pooled' : 'W3/Bilateral']}
            contentStyle={getTooltipStyle()}
          />
          <Legend
            formatter={(value) => value === 'w1w2' ? 'W1/W2 Pooled' : 'W3/Bilateral'}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar dataKey="w1w2" stackId="a" fill="#006837" />
          <Bar dataKey="w3" stackId="a" fill="#F37021" radius={[0, 4, 4, 0]}>
            <LabelList content={renderStackTotal} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
